import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Stop default icon 404s (react-leaflet bundler quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickCatcher({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, map.getZoom(), { duration: 0.4 });
  }, [center, map]);
  return null;
}

/**
 * Interactive Leaflet picker used in the admin "New drop" form.
 * Click anywhere on the map → fires onChange({ lat, lng }). Zoom/drag enabled
 * so admins can navigate to the exact hide spot.
 */
export default function ZonePicker({ lat, lng, radius = 1000, onChange }) {
  const hasPoint =
    lat !== "" && lat !== null && lat !== undefined && !isNaN(Number(lat)) &&
    lng !== "" && lng !== null && lng !== undefined && !isNaN(Number(lng));

  const center = useMemo(
    () => (hasPoint ? [Number(lat), Number(lng)] : [36.5484, -82.5618]),
    [hasPoint, lat, lng],
  );
  const initialCenter = useRef(center);

  const pick = (newLat, newLng) => {
    onChange({ lat: Number(newLat.toFixed(6)), lng: Number(newLng.toFixed(6)) });
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => pick(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="loot-card-flat p-0 overflow-hidden" data-testid="zone-picker">
      <div className="relative" style={{ height: 280 }}>
        <MapContainer
          center={initialCenter.current}
          zoom={hasPoint ? 15 : 12}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: "#0f1f12" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          <ClickCatcher onPick={pick} />
          {hasPoint && <Recenter center={[Number(lat), Number(lng)]} />}
          {hasPoint && (
            <>
              <Marker position={[Number(lat), Number(lng)]} />
              <Circle
                center={[Number(lat), Number(lng)]}
                radius={Math.max(10, Number(radius) || 1000)}
                pathOptions={{
                  color: "#76B82A",
                  fillColor: "#76B82A",
                  fillOpacity: 0.12,
                  weight: 2,
                }}
              />
            </>
          )}
        </MapContainer>
        <div
          className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border pointer-events-none"
          style={{
            borderColor: "rgba(118,184,42,0.6)",
            background: "rgba(15,15,15,0.8)",
            color: "#76B82A",
          }}
        >
          Click to pin · drag to move
        </div>
        <button
          type="button"
          onClick={locateMe}
          className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border"
          style={{
            borderColor: "rgba(215,166,42,0.7)",
            background: "rgba(15,15,15,0.85)",
            color: "#D7A62A",
          }}
          data-testid="zone-picker-locate"
        >
          Use my location
        </button>
      </div>
      <div className="p-3 flex items-center justify-between font-mono text-xs text-[#F5F2E8]/70">
        <span>
          {hasPoint ? (
            <>
              <span className="text-[#76B82A]">{Number(lat).toFixed(5)}</span>
              {" , "}
              <span className="text-[#76B82A]">{Number(lng).toFixed(5)}</span>
            </>
          ) : (
            <span className="text-[#F5F2E8]/50">No point picked</span>
          )}
        </span>
        <span className="text-[#F5F2E8]/50">
          radius {Math.max(10, Number(radius) || 1000)} m
        </span>
      </div>
    </div>
  );
}
